import BoltOutlined from '@mui/icons-material/BoltOutlined'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import HistoryOutlined from '@mui/icons-material/HistoryOutlined'
import LanguageOutlined from '@mui/icons-material/LanguageOutlined'
import LinkOutlined from '@mui/icons-material/LinkOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'

import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Typography } from '@/shared/components/ui/typography'
import { useUserContext } from '@/shared/contexts/userContext'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import {
  ApiPlaybookListItem,
  PlaybookOutputType,
  PlaybookUserAction,
} from '@/types/playbook'

import React from 'react'
import { Link } from 'react-router-dom'

type PlaybookCardProps = {
  playbook: ApiPlaybookListItem
  onClick?: () => void
  variant?: 'default' | 'withMenu'
  onPlaybookAction?: (action: PlaybookUserAction) => void
}

export const PlaybookCard: React.FC<PlaybookCardProps> = ({
  playbook,
  variant = 'default',
  onPlaybookAction,
}) => {
  const { flags: [
    isRenamingEnabled,
    isDuplicationEnabled,
    isVersionControlEnabled,
    isVisibilityEnabled,
    isDeleteEnabled,
    isPlaybookSharingEnabled,
  ] } = useFeatureFlagsWithWorkspace([
    'playbookRenaming',
    'playbookDuplication',
    'playbookVersionControl',
    'playbookVisibilityManagement',
    'playbookDeletion',
    'playbookSharing',
  ])

  const { user } = useUserContext()
  const isOwner =
    playbook?.createdBy === user?._id ||
    playbook?.collaborators?.[user?._id] === 'owner'
  const canEdit = playbook?.collaborators?.[user?._id] === 'edit' || isOwner
  const { isPublic, outputType } = playbook

  return (
    <Link to={`/playbooks/${playbook._id}`} className="block h-full">
      <Card className="h-full cursor-pointer rounded-2xl border bg-grey-02 p-4 transition-shadow hover:shadow-md md:p-6">
        <div className="flex h-full flex-col justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <div className="self-end">
                {isPublic ? (
                  <LanguageOutlined sx={{ fontSize: 16 }} />
                ) : (
                  <LockOutlined sx={{ fontSize: 16 }} />
                )}
              </div>
              <Typography variant="h5" className="font-medium">
                {playbook.title}
              </Typography>
            </div>
            <Typography variant="body2" className="line-clamp-3">
              {playbook.description}
            </Typography>
            {/** TODO: add playbook steps statuses  */}
          </div>
          <div className="mt-9 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-sm font-medium text-mydra-gray sm:gap-2 sm:px-4 sm:py-2 sm:text-base">
              <div className="flex h-3 items-center justify-center">
                {outputType === PlaybookOutputType.ToolDiscovery ? (
                  <BoltOutlined sx={{ fontSize: 12 }} />
                ) : (
                  <BookOutlinedIcon sx={{ fontSize: 12 }} />
                )}
              </div>
              <Typography variant="caption" className="font-medium">
                {outputType === PlaybookOutputType.ToolDiscovery
                  ? 'AI Tool Discovery'
                  : 'AI Playbook'}
              </Typography>
            </span>

            {variant === 'withMenu' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="cursor-pointer bg-grey-03/10"
                    onClick={(e) => e.preventDefault()}
                  >
                    <MoreHorizOutlined sx={{ fontSize: 16 }} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.preventDefault()}
                >
                  {isRenamingEnabled && (
                    <DropdownMenuItem
                      disabled={!canEdit} // TODO: add workspace-level permission check
                      className="cursor-pointer"
                      onClick={() => onPlaybookAction?.('rename')}
                    >
                      <EditOutlined sx={{ fontSize: 16 }} className="mr-2" />
                      Rename playbook
                    </DropdownMenuItem>
                  )}
                  {isPlaybookSharingEnabled && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onPlaybookAction?.('copyLink')}
                    >
                      <LinkOutlined sx={{ fontSize: 16 }} className="mr-2" />
                      Copy playbook link
                    </DropdownMenuItem>
                  )}
                  {isDuplicationEnabled && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onPlaybookAction?.('duplicate')}
                    >
                      <ContentCopyOutlined
                        sx={{ fontSize: 16 }}
                        className="mr-2"
                      />
                      Duplicate playbook
                    </DropdownMenuItem>
                  )}
                  {isVersionControlEnabled && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => onPlaybookAction?.('versionHistory')}
                    >
                      <HistoryOutlined sx={{ fontSize: 16 }} className="mr-2" />
                      Version history
                    </DropdownMenuItem>
                  )}
                  {isVisibilityEnabled && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => onPlaybookAction?.('visibility')}
                        disabled={!canEdit}
                      >
                        <VisibilityOutlined
                          sx={{ fontSize: 16 }}
                          className="mr-2"
                        />
                        Visibility settings
                      </DropdownMenuItem>
                    </>
                  )}
                  {isDeleteEnabled && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={!canEdit}
                        className="cursor-pointer"
                        onClick={() => onPlaybookAction?.('delete')}
                      >
                        <DeleteOutlined
                          sx={{ fontSize: 16 }}
                          className="mr-2"
                        />
                        Delete playbook
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </Card>
    </Link>
  )
}
